import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type {
  CashTransactionGroupType,
  Gender,
  PendingChargeStatus,
} from '@pflegenavi/shared/api';
import { Filter } from '../filter';
import type { ReceiptTypeName } from '../transaction-phoenix/api';

export type PayoutType =
  | 'earnings'
  | 'direct_spend'
  | 'pliant_spend'
  | 'direct_and_pliant_spend';

export interface PayoutTypeAmount {
  count: number;
  amount: number;
}

export interface AvailableFundsDetails {
  by_payout_type: Partial<Record<PayoutType, PayoutTypeAmount>>;
  total: number;
}

export interface WalletBalanceDetails {
  amount_pending_payout: number;
  amount_blocked_from_payout: number;
  total: number;
  by_payout_type: Partial<Record<PayoutType, PayoutTypeAmount>>;
}

export interface FinanceResult {
  data: {
    nursing_home_id: string;
    payout_mode: 'separate' | 'unified';
    total_resident_balance: number;
    mangopay: {
      wallet: number;
      payins: number;
      payouts: number;
      available_funds_amount: number;
    };
    wallet_balance_details: WalletBalanceDetails;
    available_funds_details: AvailableFundsDetails;
    bank: {
      total: number;
    };
    cash: {
      total: number;
    };
    receipts: {
      draft: number;
    };
  };
}

export interface GetPayoutsResult {
  data: Array<{
    id: string;
    inserted_at: Date;
    transferred: Date | undefined;
    statement_descriptor: string;
    iban_last_4: string;
    amount: number;
    currency: string;
    state: 'CREATED' | 'SUCCEEDED' | 'FAILED';
    payout_type: PayoutType;
  }>;
  meta: {
    total_count: number;
    total_pages: number;
  };
}

export interface GetPayoutsParams {
  params: {
    nursing_home_id: string;
    page: number;
    pageSize: number;
    state: 'CREATED' | 'SUCCEEDED' | 'FAILED' | undefined;
    orderBy?: 'transferred';
    orderDirection?: 'asc' | 'desc';
    ids?: string[];
  };
}

export interface PayoutReconciliationEntry {
  id: string;
  mangopay_resource_id: string;
  mangopay_transaction_type: 'TRANSFER' | 'PAYOUT';
  mangopay_transaction_nature: 'REGULAR' | 'REFUND';
  amount: number;
  currency: string;
  performed_at: string;
  // Null for the payout itself and for payout refunds.
  receipt: {
    id: string;
    receipt_date: string;
    amount: number;
    resident: { firstname: string; lastname: string };
    service_provider_name: string | null;
    receipt_type_name: ReceiptTypeName | null;
  } | null;
}

export interface GetPayoutReconciliationResult {
  data: PayoutReconciliationEntry[];
}

export interface GetPayoutReconciliationParams {
  params: {
    nursing_home_id: string;
    payout_id: string;
  };
}

export interface GetPayinsResult {
  data: Array<{
    id: string;
    amount: number;
    action_timestamp: Date;
    updated_at: Date;
    charge_date: Date;
    pending_charge_status: Omit<
      PendingChargeStatus,
      | PendingChargeStatus.initializing
      | PendingChargeStatus.accountMissing
      | PendingChargeStatus.dataMissing
    >;
    employee: {
      id: string;
      first_name: string;
      last_name: string;
    };
    resident: {
      id: string;
      firstname: string;
      lastname: string;
      gender: Gender;
    };
  }>;
  meta: {
    total_count: number;
    total_pages: number;
  };
}

export type PayinStatusFilter =
  | 'Success'
  | 'Failed'
  | 'Frozen'
  | 'Upcoming'
  | 'Pending';

export type PayinSortColumn = 'charge_date' | 'updated_at';

export interface PayinFilters {
  /** ISO datetime, inclusive; matched against the action timestamp. */
  actionTimestampFrom?: string;
  /** Exclusive: the start of the day after the last day to include. */
  actionTimestampTo?: string;
}

export interface GetPayinsParams {
  params: {
    nursing_home_id: string;
    page: number;
    pageSize: number;
    pending_charge_status?: PayinStatusFilter[];
    charge_ids?: string[];
    /** Defaults to the charge date, newest first. */
    orderBy?: PayinSortColumn;
    orderDirection?: 'asc' | 'desc';
    filters?: PayinFilters;
  };
}

export interface ResidentWithBalance {
  id: string;
  firstname: string;
  lastname: string;
  balance: number;
  due_date: Date;
}

export interface ReceiptStatusCount {
  count: number;
  total: number;
}

export interface ReceiptTypeCount {
  count: number;
  total: number;
}

export interface GetDashboardResult {
  data: {
    receipts: Record<string, ReceiptStatusCount>;
    negative_balance: {
      count: number;
      residents: ResidentWithBalance[];
    };
    balance_below_threshold: {
      count: number;
      residents: ResidentWithBalance[];
    };
    receipt_types: Record<string, ReceiptTypeCount>;
    date_range: {
      from_date: Date;
      to_date: Date;
    };
  };
}

export interface GetDashboardParams {
  params: {
    nursing_home_id: string;
    month: string; // Format: "YYYY-MM"
  };
}

export interface BalanceChange {
  start_balance: number;
  end_balance: number;
  change: number;
}

export interface CashListBalanceChange extends BalanceChange {
  cash_list_id: string;
}

export interface GetReportingOverviewResult {
  data: {
    from: string;
    to: string;
    cash_lists: CashListBalanceChange[];
    /** null if the legal entity has no Pliant organization */
    pliant: { change: number } | null;
    wallet: BalanceChange;
  };
}

export interface GetReportingOverviewParams {
  params: {
    nursing_home_id: string;
    /** ISO datetime, inclusive */
    from: string;
    /** ISO datetime, exclusive: the start of the day after the last day to include. */
    to: string;
  };
}

export interface BookkeepingChange {
  source: 'pliant' | 'cash_group';
  legal_entity_id: string;
  nursing_home_id: string;
  pliant_transaction_id: string | null;
  group_id: string | null;
  group_ids: string[];
  /** null for Pliant rows */
  group_type: CashTransactionGroupType | null;
  /** null for Pliant rows */
  cash_list_name: string | null;
  bookable_amount_change: number;
  settlement_loss_change: number;
  ongoing_payin_amount_change: number;
  bookkeeping_relevant: boolean;
  /** Breakdown of how the amounts above arise; 0 where a source has none. */
  billing_amount_change: number;
  amount_change: number;
  receipt_submission_amount_change: number;
  receipt_cancellation_amount_change: number;
  settled_receipt_amount_change: number;
  settled_receipt_settlement_amount_change: number;
  settlement_linked_settled_receipt_settlement_amount_change: number;
  linked_cash_list_transaction_amount_change: number;
  nursing_home_payout_amount_change: number;
  nursing_home_payout_refund_amount_change: number;
  opened_payin_amount_change: number;
  closed_payin_amount_change: number;
  receipt_submission_ids: string[];
  settled_receipt_ids: string[];
  settlement_linked_settled_receipt_ids: string[];
  cancelled_receipt_ids: string[];
  linked_cash_list_transaction_ids: string[];
  nursing_home_payout_ids: string[];
  nursing_home_payout_refund_ids: string[];
  opened_payin_charge_ids: string[];
  closed_payin_charge_ids: string[];
}

export interface GetBookkeepingChangesResult {
  data: BookkeepingChange[];
}

export interface GetBookkeepingChangesParams {
  params: ({ nursing_home_id: string } | { legal_entity_id: string }) & {
    /** ISO datetime, inclusive */
    period_start: string;
    /** ISO datetime, exclusive */
    period_end: string;
    /** Substring match on the cash list name; does not narrow Pliant rows. */
    cash_list_name: string;
  };
}

export interface ImmediateTransferFee {
  legal_entity_id: string;
  nursing_home_id: string;
  pliant_transaction_id: string;
  /** Negative when the fee is lost, positive when a reversal offsets it. */
  immediate_transfer_fee_amount_change: number;
  group_ids: string[];
}

export interface GetImmediateTransferFeesResult {
  data: ImmediateTransferFee[];
}

export interface GetImmediateTransferFeesParams {
  params: ({ nursing_home_id: string } | { legal_entity_id: string }) & {
    /** ISO datetime, inclusive */
    period_start: string;
    /** ISO datetime, exclusive */
    period_end: string;
  };
}

export interface EarningsChange {
  legal_entity_id: string;
  nursing_home_id: string;
  receipt_id: string;
  /** Receipt amount when submitted in the period */
  submission_amount_change: number;
  /** Negated receipt amount when cancelled in the period */
  cancellation_amount_change: number;
  earnings_submitted_change: number;
  /** Receipt amount paid out by a succeeded payout in the period */
  payout_amount_change: number;
  /** Negative when a payout in the period takes back a cancelled receipt */
  payout_cancellation_amount_change: number;
  earnings_paid_out_change: number;
  payout_ids: string[];
}

export interface GetEarningsChangesResult {
  data: EarningsChange[];
}

export interface GetEarningsChangesParams {
  params: ({ nursing_home_id: string } | { legal_entity_id: string }) & {
    /** ISO datetime, inclusive */
    period_start: string;
    /** ISO datetime, exclusive */
    period_end: string;
  };
}

export interface IReportingPhoenixApi extends Api {
  getFinance(dataIn: {
    params: {
      nursing_home_id: string;
    };
  }): Promise<FinanceResult>;
  getPayouts(params: GetPayoutsParams): Promise<GetPayoutsResult>;
  getPayoutReconciliation(
    params: GetPayoutReconciliationParams
  ): Promise<GetPayoutReconciliationResult>;
  getPayins(params: GetPayinsParams): Promise<GetPayinsResult>;
  getDashboard(params: GetDashboardParams): Promise<GetDashboardResult>;
  getOverview(
    params: GetReportingOverviewParams
  ): Promise<GetReportingOverviewResult>;
  getBookkeepingChanges(
    params: GetBookkeepingChangesParams
  ): Promise<GetBookkeepingChangesResult>;
  getImmediateTransferFees(
    params: GetImmediateTransferFeesParams
  ): Promise<GetImmediateTransferFeesResult>;
  getEarningsChanges(
    params: GetEarningsChangesParams
  ): Promise<GetEarningsChangesResult>;
}

export class ReportingPhoenixApi implements IReportingPhoenixApi {
  baseUrl: string;
  headers: Headers;

  getFinance: IReportingPhoenixApi['getFinance'];
  getPayouts: IReportingPhoenixApi['getPayouts'];
  getPayoutReconciliation: IReportingPhoenixApi['getPayoutReconciliation'];
  getPayins: IReportingPhoenixApi['getPayins'];
  getDashboard: IReportingPhoenixApi['getDashboard'];
  getOverview: IReportingPhoenixApi['getOverview'];
  getBookkeepingChanges: IReportingPhoenixApi['getBookkeepingChanges'];
  getImmediateTransferFees: IReportingPhoenixApi['getImmediateTransferFees'];
  getEarningsChanges: IReportingPhoenixApi['getEarningsChanges'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.headers = new Headers({
      'content-type': 'application/json',
      'x-pn-stripemode': 'live',
    });

    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);

    this.getFinance = get({
      authContext,
      url: (params) =>
        `${this.baseUrl}/reporting/nursing-home/${params.nursing_home_id}/finance`,
    });

    this.getPayouts = get({
      authContext,
      headers: this.headers,
      url: (params) => {
        const filter = new Filter();
        filter.fieldIfPresent('mangopay_state', '==', params.state);
        if (params.ids) {
          filter.field('id', 'in', params.ids);
        }

        const query = filter.build();
        query.append('page', (params.page + 1).toString());
        query.append('page_size', params.pageSize.toString());

        if (params.orderBy) {
          query.append('order_by[]', params.orderBy);
          query.append('order_directions[]', params.orderDirection ?? 'desc');
        }

        return `${this.baseUrl}/reporting/nursing-home/${
          params.nursing_home_id
        }/payouts?${query.toString()}`;
      },
    });

    this.getPayoutReconciliation = get({
      authContext,
      headers: this.headers,
      url: (params) =>
        `${this.baseUrl}/reporting/nursing-home/${params.nursing_home_id}/payouts/${params.payout_id}/reconciliation`,
    });

    this.getPayins = get({
      authContext,
      headers: this.headers,
      url: (params) => {
        const filter = new Filter();

        // Bare `if` (no length check) is intentional: an explicit `[]`
        // still sends the (valueless) `in` filter, matching zero rows,
        // rather than being treated as absent like `fieldIfPresent` would.
        if (params.pending_charge_status) {
          filter.field(
            'pending_charge_status',
            'in',
            params.pending_charge_status
          );
        }
        if (params.charge_ids) {
          filter.field('charge_id', 'in', params.charge_ids);
        }
        filter.fieldIfPresent(
          'action_timestamp',
          '>=',
          params.filters?.actionTimestampFrom
        );
        filter.fieldIfPresent(
          'action_timestamp',
          '<',
          params.filters?.actionTimestampTo
        );

        const query = filter.build();
        query.append('page', (params.page + 1).toString());
        query.append('page_size', params.pageSize.toString());

        const orderBy = params.orderBy ?? 'charge_date';
        const orderDirection = params.orderDirection ?? 'desc';
        const tieBreakers =
          orderBy === 'charge_date' ? ['updated_at', 'id'] : ['id'];
        for (const column of [orderBy, ...tieBreakers]) {
          query.append('order_by[]', column);
          query.append('order_directions[]', orderDirection);
        }

        return `${this.baseUrl}/reporting/nursing-home/${
          params.nursing_home_id
        }/payins?${query.toString()}`;
      },
    });

    this.getDashboard = get({
      authContext,
      headers: this.headers,
      url: (params) => {
        const query = new URLSearchParams();

        if (params.month) {
          query.append('month', params.month);
        }

        return `${this.baseUrl}/reporting/nursing-home/${
          params.nursing_home_id
        }/dashboard${query.toString() ? `?${query.toString()}` : ''}`;
      },
    });

    this.getOverview = get({
      authContext,
      url: (params) => {
        const query = new URLSearchParams({ from: params.from, to: params.to });
        return `${this.baseUrl}/reporting/nursing-home/${
          params.nursing_home_id
        }/overview?${query.toString()}`;
      },
    });

    // No page params: the endpoint has no default limit, so this returns every row.
    this.getBookkeepingChanges = get({
      authContext,
      url: (params) => {
        const query = new Filter()
          .buildScope(
            'nursing_home_id' in params
              ? { nursingHomeId: params.nursing_home_id }
              : { legalEntityId: params.legal_entity_id }
          )
          .build();
        query.append('period_start', params.period_start);
        query.append('period_end', params.period_end);
        query.append('cash_list_name', params.cash_list_name);
        return `${
          this.baseUrl
        }/reporting/bookkeeping-changes?${query.toString()}`;
      },
    });

    // No page params: the endpoint has no default limit, so this returns every row.
    this.getImmediateTransferFees = get({
      authContext,
      url: (params) => {
        const query = new Filter()
          .buildScope(
            'nursing_home_id' in params
              ? { nursingHomeId: params.nursing_home_id }
              : { legalEntityId: params.legal_entity_id }
          )
          .build();
        query.append('period_start', params.period_start);
        query.append('period_end', params.period_end);
        return `${
          this.baseUrl
        }/reporting/immediate-transfer-fees?${query.toString()}`;
      },
    });

    // No page params: the endpoint has no default limit, so this returns every row.
    this.getEarningsChanges = get({
      authContext,
      url: (params) => {
        const query = new Filter()
          .buildScope(
            'nursing_home_id' in params
              ? { nursingHomeId: params.nursing_home_id }
              : { legalEntityId: params.legal_entity_id }
          )
          .build();
        query.append('period_start', params.period_start);
        query.append('period_end', params.period_end);
        return `${this.baseUrl}/reporting/earnings-changes?${query.toString()}`;
      },
    });
  }
}
